import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CARRIER_VALUE_TO_CARRIER, IMO } from "@aam/shared";
import { AgentForm } from "../AgentForm.js";
import { LoadingStatus, Skeleton } from "../ui/index.js";
import { createPortalDraft, loadDraft } from "../../hooks/useDraft.js";
import { getSessionToken, useSession } from "../../hooks/useSession.js";
import {
  PORTAL_FORM_VARIANT,
  remapPortalDraftStep,
} from "../../lib/normalizeDraft.js";
import { useFormStore } from "../../store/formStore.js";
import {
  autofillUpdatesForBlanks,
  loadContractAutofill,
  type PortalAutofillPlan,
} from "../../lib/contractAutofill.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

export function PortalApplicationPage() {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { agent } = useSession();
  const updateMultipleFields = useFormStore((state) => state.updateMultipleFields);
  const encryptedFields = useFormStore((state) => state.encryptedFields);
  const [ready, setReady] = useState(false);
  const [initialStep, setInitialStep] = useState(0);
  const [unavailableCarriers, setUnavailableCarriers] = useState<Record<string, string>>({});
  const [autofill, setAutofill] = useState<PortalAutofillPlan | null>(null);
  // Creating the first draft navigates to its token, which re-runs the
  // initializer. The plan does not change mid-application, so fetch it once.
  const autofillRequest = useRef<Promise<PortalAutofillPlan | null> | null>(null);

  useEffect(() => {
    const sessionToken = getSessionToken();
    if (!sessionToken) return;
    let cancelled = false;

    void fetch(`${API_BASE}/api/agents/me`, {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then(async (response) => {
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load contracts");
        return json.data;
      })
      .then((data) => {
        if (cancelled) return;
        const contracts = Array.isArray(data.contracts) ? data.contracts : [];
        // A carrier already in flight cannot be requested again, so the picker
        // greys it out and says which kind of contract is in the way.
        const unavailable = Object.fromEntries(
          contracts
            .filter(
              (contract: { status?: string }) =>
                contract.status === "PENDING" || contract.status === "ACTIVE"
            )
            .map((contract: { carrier: string; status?: string }) => [
              contract.carrier,
              contract.status === "ACTIVE" ? "Active contract" : "Pending contract",
            ])
        );
        const eligible = Array.isArray(data.organization?.eligibleCarriers)
          ? (data.organization.eligibleCarriers as string[])
          : [];
        if (eligible.length > 0) {
          const eligibleSet = new Set(eligible);
          const organizationName =
            typeof data.organization?.brandName === "string"
              ? data.organization.brandName
              : "your organization";
          for (const carrier of Object.keys(CARRIER_VALUE_TO_CARRIER)) {
            if (!eligibleSet.has(carrier)) {
              unavailable[carrier] = `Not available through ${organizationName}`;
            }
          }
        }
        setUnavailableCarriers(unavailable);
      })
      .catch(() => {
        // Non-fatal — the backend still enforces this at submit time.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initialize() {
      // Everything this agent's profile and previous contracts can supply.
      // Fetched before the draft is written so a returning agent's first save
      // already carries their details instead of populating a step later.
      autofillRequest.current ??= loadContractAutofill();
      const plan = await autofillRequest.current;
      if (cancelled) return;
      setAutofill(plan);

      if (!token) {
        useFormStore.getState().resetForm();
        const identity = {
          email: agent?.email ?? "",
          firstName: agent?.firstName ?? "",
          lastName: agent?.lastName ?? "",
          fullName: [agent?.firstName, agent?.lastName].filter(Boolean).join(" "),
          phone: agent?.phone ?? "",
          npn: agent?.npn ?? "",
          formVariant: PORTAL_FORM_VARIANT,
        };
        const initialFormData = {
          ...identity,
          ...autofillUpdatesForBlanks(identity, plan),
        };
        updateMultipleFields(initialFormData);
        const draftToken = await createPortalDraft({
          formData: initialFormData,
          currentStep: 0,
          email: agent?.email,
        });
        if (!cancelled) {
          navigate(`/my-agent/contracts/apply/${draftToken}`, { replace: true });
        }
        return;
      }

      const draft = await loadDraft(token);
      if (!draft) {
        navigate("/my-agent/contracts/apply", { replace: true });
        return;
      }
      // Do not run public pre-v3 step migration on portal drafts — those
      // indices already match either the old public portal form or the
      // identity-less portal schema (after remapPortalDraftStep).
      const formData = { ...(draft.formData as Record<string, unknown>) };
      if (
        !(typeof formData.fullName === "string" && formData.fullName.trim()) &&
        (typeof formData.firstName === "string" || typeof formData.lastName === "string")
      ) {
        const firstName =
          typeof formData.firstName === "string" ? formData.firstName.trim() : "";
        const lastName =
          typeof formData.lastName === "string" ? formData.lastName.trim() : "";
        const composed = [firstName, lastName].filter(Boolean).join(" ");
        if (composed) formData.fullName = composed;
      }
      const portalStep = remapPortalDraftStep(formData, draft.currentStep);
      updateMultipleFields({
        ...formData,
        // Only blanks — a resumed draft's own answers always win.
        ...autofillUpdatesForBlanks(formData, plan),
        // Login email is not a form answer. A profile change should show here
        // even if the draft still has the old address.
        email: agent?.email ?? formData.email,
        formVariant: PORTAL_FORM_VARIANT,
      });
      if (!cancelled) {
        setInitialStep(portalStep);
        setReady(true);
      }
    }

    void initialize();
    return () => {
      cancelled = true;
    };
  }, [agent, navigate, token, updateMultipleFields]);

  if (!ready) {
    return (
      <main className="mx-auto w-full max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8" role="status" aria-busy="true">
        <LoadingStatus label="Loading your contract request" />
        <div className="border-b-2 border-border-default pb-6">
          <Skeleton className="h-3 w-36" />
          <Skeleton className="mt-3 h-12 w-96 max-w-full" />
          <Skeleton className="mt-4 h-5 w-2/3" />
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-36 w-full rounded-card" />
          ))}
        </div>
        <Skeleton className="ml-auto h-16 w-full max-w-xl rounded-card" />
      </main>
    );
  }

  return (
    <AgentForm
      variant="portal"
      imo={IMO.PINNACLE}
      initialStep={initialStep}
      draftToken={token}
      unavailableCarriers={unavailableCarriers}
      autofill={autofill}
      onDraftCreated={(draftToken, nextStep) => {
        navigate(`/my-agent/contracts/apply/${draftToken}`, { replace: true });
        setInitialStep(nextStep);
      }}
      onStepChange={(nextStep) => {
        if (token) navigate(`/my-agent/contracts/apply/${token}`, { replace: true });
        setInitialStep(nextStep);
      }}
      onSubmit={async (formData) => {
        const sessionToken = getSessionToken();
        if (!sessionToken) throw new Error("Your session has expired. Please sign in again.");
        const response = await fetch(`${API_BASE}/api/submissions/portal`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            carriers: (formData.selectedCarriers as string[]) ?? [],
            formData,
            encryptedFields,
          }),
        });
        const json = await response.json();
        if (!response.ok || !json.success) {
          const error = new Error(json.error || "Unable to submit contract request") as Error & {
            fieldErrors?: Array<{ fieldId?: string; label?: string; message?: string }>;
          };
          error.fieldErrors = json.fieldErrors;
          throw error;
        }
      }}
      onBack={() => navigate("/my-agent/contracts")}
    />
  );
}
